import { memo, useMemo } from 'react';
import { ChakraFlex, ChakraStack, DropdownV2, Text } from '@equip.health/ui';
import SelectableUserAvatarBlock from '../SelectableUserAvatarBlock';
import { getEnrichedUser, getIntakeAppointmentTypeOptions } from '../utils';
import { CareTeamProviderType } from '~/lib/constants/patients.constants';
import SkeletonLoaderList from '~/components/common/SkeletonLoaderList';

const TOP_LEVEL_ID =
  'provider-assignment-attendee-and-appointment-type-selection';

interface AttendeeAndAppointmentTypeSelectionProps {
  appointmentType: Partial<AppointmentType>;
  attendees: FamilyAttendeeWithSelection[];
  isFetchingAttendees: boolean;
  onAppointmentTypeChange: (appointmentType: Partial<AppointmentType>) => void;
  onAttendeesChange: (attendees: FamilyAttendeeWithSelection[]) => void;
  patient: Patient;
  providerType: CareTeamProviderType;
}

const AttendeeAndAppointmentTypeSelection = ({
  appointmentType,
  attendees,
  isFetchingAttendees,
  onAppointmentTypeChange,
  onAttendeesChange,
  patient,
  providerType,
}: AttendeeAndAppointmentTypeSelectionProps) => {
  const allIntakeOptions = useMemo(
    () => getIntakeAppointmentTypeOptions(providerType),
    [providerType],
  );

  const intakeAppointmentTypeOptions = useMemo(() => {
    return allIntakeOptions.map((t) => ({
      label: `${t.appointmentType}, ${t.durationInMinutes} minutes`,
      value: t.appointmentTypeLabel,
    }));
  }, [allIntakeOptions]);

  const handleAppointmentTypeOptionClick = (appointmentTypeLabel: string) =>
    onAppointmentTypeChange(
      allIntakeOptions.find(
        (t) => t.appointmentTypeLabel === appointmentTypeLabel,
      ),
    );

  const handleAttendeeToggle = (attendeeId: string) => {
    const updatedAttendees = attendees.map((a) =>
      a.externalId === attendeeId ? { ...a, isSelected: !a.isSelected } : a,
    );
    onAttendeesChange(updatedAttendees);
  };

  const renderAttendee = (attendee: FamilyAttendeeWithSelection) => (
    <SelectableUserAvatarBlock
      id={`${TOP_LEVEL_ID}__${attendee.externalId}`}
      isSelected={attendee.isSelected}
      key={attendee.externalId}
      onToggle={() => handleAttendeeToggle(attendee.externalId)}
      showPronouns
      showPronunciation
      user={getEnrichedUser(attendee, patient)}
      variant="attendeeCard"
    />
  );

  return (
    <ChakraFlex
      data-testid={TOP_LEVEL_ID}
      direction="column"
      gap="24px"
      padding="0"
      width="100%"
    >
      <Text color="neutral.secondary" variant="caption">
        To begin, please confirm the length and attendees for the appointment
      </Text>
      <DropdownV2
        id={`${TOP_LEVEL_ID}__appointment-type`}
        isDisabled={intakeAppointmentTypeOptions.length < 2}
        isResettable={false}
        menuOptions={intakeAppointmentTypeOptions}
        onOptionClick={handleAppointmentTypeOptionClick}
        value={appointmentType?.appointmentTypeLabel}
      />
      {isFetchingAttendees ? (
        <SkeletonLoaderList
          direction="column"
          gap="12px"
          id={TOP_LEVEL_ID}
          itemCount={2}
          itemHeight="78px"
          itemWidth="100%"
        />
      ) : (
        <ChakraStack spacing="12px">
          {attendees.filter((a) => a.isRequired).map(renderAttendee)}
          {attendees.filter((a) => !a.isRequired).map(renderAttendee)}
        </ChakraStack>
      )}
    </ChakraFlex>
  );
};

export default memo(AttendeeAndAppointmentTypeSelection);
